import React, { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, X, Power } from 'lucide-react';
import {
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useToggleTemplateActive,
} from '../../../hooks/useTemplates';
import { useToast } from '../../../contexts/ToastContext';
import MergeFieldPicker from './MergeFieldPicker';
import CodeMirrorEditor from './CodeMirrorEditor';
import TemplatePreview from './TemplatePreview';

const CATEGORIES = ['confirmation', 'reminder', 'payment', 'promo', 'other'];

function EmailTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const toast = useToast();
  const subjectRef = useRef(null);
  const codeMirrorRef = useRef(null);
  const activeFieldRef = useRef('body');

  const { template, isLoading: loadingTemplate } = useTemplate(id);
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const toggleMutation = useToggleTemplateActive();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      category: 'confirmation',
      subject_tpl: '',
      body_tpl: '',
      required_fields: [],
    },
  });

  const watchedSubject = watch('subject_tpl');
  const watchedBody = watch('body_tpl');

  // Load template data into form
  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        category: template.category,
        subject_tpl: template.subject_tpl,
        body_tpl: template.body_tpl,
        required_fields: template.required_fields || [],
      });
    }
  }, [template, reset]);

  const onSubmit = async (data) => {
    try {
      if (isNew) {
        const result = await createMutation.mutateAsync(data);
        toast.success('Template created successfully');
        navigate(`/admin/email/templates/${result.data.id}`, { replace: true });
      } else {
        await updateMutation.mutateAsync({ id, ...data });
        toast.success('Template saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save template');
    }
  };

  const handleToggleActive = async () => {
    if (!id) return;
    try {
      const result = await toggleMutation.mutateAsync(id);
      toast.success(`Template ${result.data.is_active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error('Failed to toggle template status');
    }
  };

  const handleInsertField = useCallback((text) => {
    if (activeFieldRef.current === 'subject') {
      // Insert into subject input at cursor
      const el = subjectRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const currentValue = el.value;
      const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
      setValue('subject_tpl', newValue, { shouldDirty: true });
      requestAnimationFrame(() => {
        el.focus();
        el.selectionStart = start + text.length;
        el.selectionEnd = start + text.length;
      });
    } else {
      // Insert into CodeMirror
      if (codeMirrorRef.current) {
        codeMirrorRef.current.insertText(text);
        // Update form value after CodeMirror updates
      }
    }
  }, [setValue]);

  const handleSubjectFocus = () => { activeFieldRef.current = 'subject'; };
  const handleBodyFocus = () => { activeFieldRef.current = 'body'; };

  const handleBodyChange = useCallback((newValue) => {
    setValue('body_tpl', newValue, { shouldDirty: true });
  }, [setValue]);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;

  if (!isNew && loadingTemplate) {
    return (
      <div className="email-page">
        <div className="email-table-skeleton">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </div>
      </div>
    );
  }

  const { ref: subjectFormRef, ...subjectRest } = register('subject_tpl', { required: 'Subject is required' });

  return (
    <div className="email-page">
      <div className="email-page-header">
        <h3>{isNew ? 'New Template' : 'Edit Template'}</h3>
        <div className="email-editor-actions-top">
          {!isNew && template && (
            <>
              <span className="email-editor-meta">
                v{template.version} &middot; {template.is_active ? 'Active' : 'Inactive'}
              </span>
              <button
                type="button"
                className={`btn btn-sm ${template.is_active ? 'btn-warning' : 'btn-success'}`}
                onClick={handleToggleActive}
                disabled={toggleMutation.isPending}
              >
                <Power size={14} />
                {template.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="email-error">
          {saveError.response?.data?.error || 'Failed to save template.'}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="email-editor-form">
        <div className="email-editor-layout">
          <div className="email-editor-main">
            <div className="email-form-section">
              <div className="email-form-row">
                <div className="email-form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className={`email-form-input ${errors.name ? 'error' : ''}`}
                    placeholder="e.g. Reservation Confirmation"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <span className="email-form-error">{errors.name.message}</span>}
                </div>
                <div className="email-form-group">
                  <label>Category</label>
                  <select
                    className="email-form-input"
                    {...register('category', { required: 'Category is required' })}
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="email-form-group">
                <div className="email-form-label-row">
                  <label>Subject</label>
                  <MergeFieldPicker onInsert={handleInsertField} />
                </div>
                <input
                  type="text"
                  className={`email-form-input ${errors.subject_tpl ? 'error' : ''}`}
                  placeholder="e.g. Your reservation at {{property.name}} is confirmed!"
                  ref={(el) => {
                    subjectFormRef(el);
                    subjectRef.current = el;
                  }}
                  onFocus={handleSubjectFocus}
                  {...subjectRest}
                />
                {errors.subject_tpl && <span className="email-form-error">{errors.subject_tpl.message}</span>}
              </div>

              <div className="email-form-group">
                <div className="email-form-label-row">
                  <label>Body (HTML + Handlebars)</label>
                  <MergeFieldPicker onInsert={handleInsertField} />
                </div>
                <div onFocus={handleBodyFocus}>
                  <CodeMirrorEditor
                    ref={codeMirrorRef}
                    value={watchedBody}
                    onChange={handleBodyChange}
                  />
                </div>
                {errors.body_tpl && <span className="email-form-error">{errors.body_tpl.message}</span>}
              </div>
            </div>

            <div className="email-form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
              >
                <Save size={16} />
                {isSaving ? 'Saving...' : 'Save Template'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/admin/email/templates')}
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>

          <div className="email-editor-preview">
            <TemplatePreview
              subjectTpl={watchedSubject}
              bodyTpl={watchedBody}
              templateId={isNew ? null : id}
            />
          </div>
        </div>
      </form>
    </div>
  );
}

export default EmailTemplateEditor;
